import type { QASearchResultDTO } from '@next-space/fe-api-idl';
import { getHierarchicalIndexes, TableOfContents } from '@tiptap-pro/extension-table-of-contents';
import type { Editor } from '@tiptap/react';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import type { FC } from 'react';
import { memo, useEffect, useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { cx } from '../../cx';
import type { PcIconName } from '../../icon-svg';
import { Icon } from '../../icon-svg';
import { useChatContext } from '../hooks/context';
import { getRefData, useChatResultData } from '../hooks/hook';
import { formatReferenceTextToHTML } from '../hooks/utils';
import '../style/markdown.css';
import { BaseExtension } from './tiptap-input';
import remarkMath from 'remark-math';
import { ReferenceExtension } from './plugin/reference-extension';
import { LoadingDots } from './loading-dots';

interface RenderMarkdownProps {
  markdown: string;
  bubbleList?: {
    name: string;
    icon: string;
    iconClassName?: string;
    onClick?: (editor: Editor) => void;
  }[];
  refResources?: QASearchResultDTO;
  className?: string;
  hidden?: boolean;
  showToc?: boolean;
  isShare?: boolean;
  functionCall?: string;
}
export const RenderMarkdown: FC<RenderMarkdownProps> = memo((props) => {
  const { markdown, bubbleList, className, hidden, showToc = true, isShare = false } = props;
  const { setTOC } = useChatContext();
  const refData = useChatResultData();

  const html = useMemo(() => {
    let processedMarkdown = markdown ?? '';
    processedMarkdown = formatReferenceTextToHTML(processedMarkdown, refData);
    processedMarkdown = processedMarkdown.replaceAll('\\(', '$');
    processedMarkdown = processedMarkdown.replaceAll('\\)', '$');
    processedMarkdown = processedMarkdown.replaceAll('\\[', '$$');
    processedMarkdown = processedMarkdown.replaceAll('\\]', '$$');
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkMath) //FIXME 这个会把$和$$都被识别为行内公式，导致的问题是本来是块公式的显示成行内公式，会不居中
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw, { passThrough: ['img', 'span'] })
      .use(rehypeStringify, { allowDangerousHtml: true });
    const result = String(processor.processSync(processedMarkdown));
    return result;
  }, [markdown, refData]);
  const editor = useEditor({
    extensions: [
      ...BaseExtension,
      ReferenceExtension,
      TableOfContents.configure({
        getIndex: getHierarchicalIndexes,
        onUpdate(content) {
          if (showToc) {
            setTOC(content);
          }
        },
      }),
    ],
    editable: false,
    content: html,
  });

  useEffect(() => {
    editor?.commands.setContent(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html]);

  return (
    <>
      {editor != null && bubbleList?.length !== 0 && (
        <BubbleMenu
          editor={editor}
          tippyOptions={{ duration: 100, arrow: false }}
          updateDelay={0}
          shouldShow={({ state }) => !state.selection.empty}
          className={cx(
            'next-modal text-grey1 flex items-center divide-x divide-grey6 py-2 text-t2',
            {
              // hidden, //用hidden是因为如果不显示这个组件会报错，hidden就不会。跟卸载editor有关
            }
          )}
        >
          {bubbleList?.map((item) => (
            <div
              key={item.name}
              onClick={() => item.onClick?.(editor)}
              className="px-2 animate-click flex items-center gap-1"
            >
              <Icon
                name={item.icon as PcIconName}
                size="small"
                className={cx('text-grey3', item.iconClassName)}
              />
              <span>{item.name}</span>
            </div>
          ))}
        </BubbleMenu>
      )}
      <EditorContent
        editor={editor}
        className={cx('markdown-body !mt-0', className, { hidden, 'markdown-body-share': isShare })}
      />
      <LoadingDots functionCall={props.functionCall} />
      {/* <div
        className={cx('markdown-body', className, { hidden, 'markdown-body-share': isShare })}
        dangerouslySetInnerHTML={{ __html: html }}
      ></div> */}
    </>
  );
});
